/**
 * CSPM print adapters — REUSE buildSimplePrintDocument (letter-letterhead).
 */
import {
  buildSimplePrintDocument,
  type SimplePrintLine,
} from './simple-document';
import type { PrintColumn, PrintDocumentModel } from '../types';
import type {
  CustomerPropertyReceipt,
  CustomerPropertyReconciliation,
  CustomerPropertyRequirement,
  CustomerPropertyReturn,
  ReconciliationPreview,
} from '@/constants/cspm';
import {
  CSPM_CLASSIFICATION_LABELS,
  CSPM_CONDITION_LABELS,
  CSPM_PURPOSE_LABELS,
  CSPM_SOURCE_TYPE_LABELS,
  type CspmClassification,
  type CspmCondition,
  type CspmPurpose,
  type CspmSourceType,
} from '@/constants/cspm';

function dash(v: unknown): string {
  const s = v == null ? '' : String(v).trim();
  return s || '—';
}

function qty(v: unknown): string {
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : '—';
}

function sourceLabel(t: string | null | undefined): string {
  if (!t) return '—';
  return CSPM_SOURCE_TYPE_LABELS[t as CspmSourceType] || t;
}

function classLabel(c: string | null | undefined): string {
  if (!c) return '—';
  return CSPM_CLASSIFICATION_LABELS[c as CspmClassification] || c;
}

function purposeLabel(p: string | null | undefined): string {
  if (!p) return '—';
  return CSPM_PURPOSE_LABELS[p as CspmPurpose] || p;
}

function conditionLabel(c: string | null | undefined): string {
  if (!c) return '—';
  return CSPM_CONDITION_LABELS[c as CspmCondition] || c;
}

function conditionNoteLines(note: string | null | undefined): string[] {
  const s = String(note ?? '').trim();
  return s ? [`Condition note: ${s}`] : [];
}

const REQ_COLUMNS: PrintColumn[] = [
  { key: 'ln', label: '#', width: '6%', align: 'center' },
  { key: 'description', label: 'Description', width: '34%' },
  { key: 'classification', label: 'Class', width: '14%' },
  { key: 'purpose', label: 'Purpose', width: '16%' },
  { key: 'qty', label: 'Expected', width: '12%', align: 'right' },
  { key: 'uom', label: 'UOM', width: '8%', align: 'center' },
  { key: 'criticality', label: 'Crit.', width: '10%', align: 'center' },
];

const CPR_COLUMNS: PrintColumn[] = [
  { key: 'ln', label: '#', width: '5%', align: 'center' },
  { key: 'description', label: 'Description', width: '28%' },
  { key: 'condition', label: 'Cond.', width: '10%' },
  { key: 'expected', label: 'Exp', width: '8%', align: 'right' },
  { key: 'received', label: 'Rcv', width: '8%', align: 'right' },
  { key: 'accepted', label: 'Acc', width: '8%', align: 'right' },
  { key: 'damaged', label: 'Dmg', width: '8%', align: 'right' },
  { key: 'rejected', label: 'Rej', width: '8%', align: 'right' },
  { key: 'uom', label: 'UOM', width: '7%', align: 'center' },
  { key: 'identity', label: 'S/N · Heat', width: '10%' },
];

const RETURN_COLUMNS: PrintColumn[] = [
  { key: 'ln', label: '#', width: '6%', align: 'center' },
  { key: 'description', label: 'Description', width: '40%' },
  { key: 'qty', label: 'Qty', width: '12%', align: 'right' },
  { key: 'uom', label: 'UOM', width: '10%', align: 'center' },
  { key: 'condition', label: 'Condition', width: '14%' },
  { key: 'identity', label: 'Identity', width: '18%' },
];

const RECON_COLUMNS: PrintColumn[] = [
  { key: 'ln', label: '#', width: '6%', align: 'center' },
  { key: 'description', label: 'Description', width: '34%' },
  { key: 'expected', label: 'Expected', width: '12%', align: 'right' },
  { key: 'received', label: 'Received', width: '12%', align: 'right' },
  { key: 'accepted', label: 'Accepted', width: '12%', align: 'right' },
  { key: 'remaining', label: 'Remaining', width: '12%', align: 'right' },
  { key: 'allocated', label: 'Allocated', width: '12%', align: 'right' },
];
export function buildRequirementPrintDocument(
  data: CustomerPropertyRequirement,
  opts?: { customerName?: string; letterhead?: boolean }
): PrintDocumentModel {
  const party = opts?.customerName || data.customer_name || 'Customer';
  const docNo = data.requirement_number || 'EOPQ';
  const lines: SimplePrintLine[] = (data.lines || []).map((ln, idx) => ({
    lineNumber: ln.line_number ?? idx + 1,
    description: ln.description || '—',
    cells: {
      ln: String(ln.line_number ?? idx + 1),
      description: ln.description || '—',
      classification: classLabel(ln.classification),
      purpose: purposeLabel(ln.purpose),
      qty: qty(ln.expected_qty),
      uom: dash(ln.uom || 'PCS'),
      criticality: dash(ln.criticality),
    },
  }));

  return buildSimplePrintDocument({
    docType: 'cspm-requirement',
    title: 'Externally Owned Property Requirement',
    documentNumber: docNo,
    partyName: party,
    templateId: opts?.letterhead === false ? 'a4-portrait' : 'letter-letterhead',
    metaLines: [
      { label: 'Requirement No.', value: docNo },
      { label: 'Customer', value: party },
      { label: 'Status', value: dash(data.status) },
      { label: 'Source', value: `${sourceLabel(data.source_document_type)} · ${dash(data.source_document_id)}` },
      ...(data.remarks ? [{ label: 'Remarks', value: data.remarks }] : []),
    ],
    columns: REQ_COLUMNS,
    lines,
    tableTitle: 'Expected property lines',
  });
}

export function buildCprPrintDocument(
  data: CustomerPropertyReceipt,
  opts?: { customerName?: string; letterhead?: boolean }
): PrintDocumentModel {
  const party = opts?.customerName || data.customer_name || 'Customer';
  const docNo = data.receipt_number || 'EOP';
  const lines: SimplePrintLine[] = (data.lines || []).map((ln, idx) => {
    const identity = [ln.serial_number, ln.heat_number, ln.lot_number, ln.batch_number]
      .map((x) => (x ? String(x).trim() : ''))
      .filter(Boolean)
      .join(' · ');
    return {
      lineNumber: ln.line_number ?? idx + 1,
      description: ln.description || '—',
      cells: {
        ln: String(ln.line_number ?? idx + 1),
        description: ln.description || '—',
        condition: conditionLabel(ln.condition_code),
        expected: qty(ln.expected_qty),
        received: qty(ln.received_qty),
        accepted: qty(ln.accepted_qty),
        damaged: qty(ln.damaged_qty),
        rejected: qty(ln.rejected_qty),
        uom: dash(ln.uom || 'PCS'),
        identity: identity || '—',
      },
      extraLines: conditionNoteLines(ln.condition_notes),
    };
  });

  return buildSimplePrintDocument({
    docType: 'cspm-cpr',
    title: 'Externally Owned Property Receipt',
    documentNumber: docNo,
    partyName: party,
    templateId: opts?.letterhead === false ? 'a4-portrait' : 'letter-letterhead',
    metaLines: [
      { label: 'EOP No.', value: docNo },
      { label: 'Customer', value: party },
      { label: 'Status', value: dash(data.status) },
      { label: 'Receipt date', value: dash(data.receipt_datetime) },
      { label: 'Source', value: `${sourceLabel(data.source_document_type)} · ${dash(data.source_document_id)}` },
      { label: 'Customer DN', value: dash(data.customer_delivery_note) },
      { label: 'Unplanned', value: data.is_unplanned ? `Yes — ${dash(data.unplanned_reason)}` : 'No' },
      { label: 'Inspection', value: dash(data.inspection_status || (data.inspection_required ? 'Required' : 'Not required')) },
      { label: 'Scan / QR payload', value: `EOP|${docNo}` },
      ...(data.remarks ? [{ label: 'Remarks', value: data.remarks }] : []),
    ],
    columns: CPR_COLUMNS,
    lines,
    tableTitle: 'Received lines (qty truth)',
    termsExtra: [
      `Machine-readable: EOP|${docNo}`,
      'Print line labels from the receipt workspace for warehouse identity tags.',
    ],
  });
}

/** Compact page-printable line label (4×6-style, not Zebra). */
export function buildCprLineLabelDocument(params: {
  receipt: CustomerPropertyReceipt;
  line: NonNullable<CustomerPropertyReceipt['lines']>[number];
  customerName?: string;
}): PrintDocumentModel {
  const docNo = params.receipt.receipt_number || 'EOP';
  const party = params.customerName || params.receipt.customer_name || 'Customer';
  const ln = params.line;
  const identity = [ln.serial_number, ln.heat_number, ln.lot_number]
    .map((x) => (x ? String(x).trim() : ''))
    .filter(Boolean)
    .join(' · ');
  const qrPayload = `EOP|${docNo}|L${ln.line_number || 1}`;

  return buildSimplePrintDocument({
    docType: 'cspm-line-label',
    title: 'Externally owned property label',
    documentNumber: `${docNo}-L${ln.line_number || 1}`,
    partyName: party,
    templateId: 'a4-portrait',
    metaLines: [
      { label: 'EOP No.', value: docNo },
      { label: 'Customer', value: party },
      { label: 'Line', value: String(ln.line_number || 1) },
      { label: 'Description', value: dash(ln.description) },
      { label: 'S/N · Heat', value: identity || '—' },
      { label: 'Condition', value: conditionLabel(ln.condition_code) },
      ...(ln.condition_notes?.trim() ? [{ label: 'Condition note', value: ln.condition_notes.trim() }] : []),
      { label: 'Scan payload', value: qrPayload },
    ],
    columns: [
      { key: 'k', label: 'Field', width: '30%' },
      { key: 'v', label: 'Value', width: '70%' },
    ],
    lines: [
      {
        lineNumber: 1,
        description: ln.description || '—',
        cells: { k: 'Qty accepted', v: `${qty(ln.accepted_qty)} ${dash(ln.uom || 'PCS')}` },
      },
      {
        lineNumber: 2,
        description: 'QR',
        cells: { k: 'QR / barcode payload', v: qrPayload },
      },
    ],
    tableTitle: 'Label fields',
    termsExtra: [`Print this page and cut to warehouse tag size. Payload: ${qrPayload}`],
  });
}

export function buildReturnPrintDocument(
  data: CustomerPropertyReturn,
  opts?: { customerName?: string; letterhead?: boolean }
): PrintDocumentModel {
  const party = opts?.customerName || data.customer_name || 'Customer';
  const docNo = data.return_number || 'EOPN';
  const lines: SimplePrintLine[] = (data.lines || []).map((ln, idx) => {
    const identity = [ln.serial_number, ln.heat_number, ln.lot_number, ln.batch_number]
      .map((x) => (x ? String(x).trim() : ''))
      .filter(Boolean)
      .join(' · ');
    return {
      lineNumber: idx + 1,
      description: ln.description || '—',
      cells: {
        ln: String(idx + 1),
        description: ln.description || '—',
        qty: qty(ln.quantity),
        uom: dash(ln.uom || 'PCS'),
        condition: conditionLabel(ln.condition_code),
        identity: identity || '—',
      },
      extraLines: conditionNoteLines(ln.condition_notes),
    };
  });

  return buildSimplePrintDocument({
    docType: 'cspm-return',
    title: 'Externally Owned Property Return',
    documentNumber: docNo,
    partyName: party,
    templateId: opts?.letterhead === false ? 'a4-portrait' : 'letter-letterhead',
    metaLines: [
      { label: 'Return No.', value: docNo },
      { label: 'Customer', value: party },
      { label: 'Status', value: dash(data.status) },
      { label: 'Return date', value: dash(data.return_datetime) },
      { label: 'Receipt', value: dash(data.receipt_id) },
      { label: 'Reason', value: dash(data.reason) },
      ...(data.remarks ? [{ label: 'Remarks', value: data.remarks }] : []),
    ],
    columns: RETURN_COLUMNS,
    lines,
    tableTitle: 'Return lines',
  });
}

export function buildReconciliationPrintDocument(
  data: CustomerPropertyReconciliation | ReconciliationPreview,
  opts?: { customerName?: string; letterhead?: boolean; documentNumber?: string }
): PrintDocumentModel {
  const isClosed = 'reconciliation_number' in data && Boolean((data as CustomerPropertyReconciliation).reconciliation_number);
  const closed = data as CustomerPropertyReconciliation;
  const preview =
    (isClosed && closed.snapshot_json
      ? (closed.snapshot_json as ReconciliationPreview)
      : (data as ReconciliationPreview)) || (data as ReconciliationPreview);

  const party = opts?.customerName || 'Customer';
  const docNo =
    opts?.documentNumber ||
    (isClosed ? closed.reconciliation_number : `PREVIEW-${preview.source_document_id?.slice(0, 8) || 'RECON'}`);

  const lines: SimplePrintLine[] = (preview.lines || []).map((ln, idx) => ({
    lineNumber: idx + 1,
    description: ln.description || '—',
    cells: {
      ln: String(idx + 1),
      description: ln.description || '—',
      expected: qty(ln.expected_qty),
      received: qty(ln.received_qty),
      accepted: qty(ln.accepted_qty),
      remaining: qty(ln.remaining_qty),
      allocated: qty(ln.allocated_qty),
    },
  }));

  return buildSimplePrintDocument({
    docType: 'cspm-reconciliation',
    title: 'Externally Owned Property Reconciliation',
    documentNumber: docNo,
    partyName: party,
    templateId: opts?.letterhead === false ? 'a4-portrait' : 'letter-letterhead',
    metaLines: [
      { label: 'Document No.', value: docNo },
      { label: 'Customer', value: party },
      { label: 'Status', value: isClosed ? dash(closed.status) : 'Preview' },
      {
        label: 'Source',
        value: `${sourceLabel(preview.source_document_type)} · ${dash(preview.source_document_id)}`,
      },
      { label: 'Expected qty', value: qty(preview.expected_qty) },
      { label: 'Accepted qty', value: qty(preview.accepted_qty) },
      { label: 'Remaining qty', value: qty(preview.remaining_qty) },
      { label: 'Unresolved qty', value: qty(preview.unresolved_qty) },
      ...(isClosed && closed.variance_approved
        ? [{ label: 'Variance', value: `Approved — ${dash(closed.variance_reason)}` }]
        : []),
    ],
    columns: RECON_COLUMNS,
    lines,
    tableTitle: 'Reconciliation balance',
  });
}
